"use client";

import { memo } from "react";
import type { ActivityEvent } from "../types";
import UserTradeRow from "./rows/UserTradeRow";
import CopyTradeRow from "./rows/CopyTradeRow";
import TrackerTradeRow from "./rows/TrackerTradeRow";
import WhaleTradeRow from "./rows/WhaleTradeRow";
import UserPostRow from "./rows/UserPostRow";
import MarketMoveRow from "./rows/MarketMoveRow";
import NewMarketRow from "./rows/NewMarketRow";
import NewEventRow from "./rows/NewEventRow";
import BlogArticleRow from "./rows/BlogArticleRow";
import DefaultRow from "./rows/DefaultRow";

/**
 * ActivityEventCard — type dispatcher for the expanded-mode feed.
 *
 * The activity feed renders many event types, each with its own row
 * component (UserTradeRow, CopyTradeRow, MarketMoveRow, …). This
 * component is a thin switch on `event.event_type` that routes to the
 * right row.
 *
 * Memoized with custom equality because the parent passes
 * `expandedEvents` (Set) and `followedTraders` (Map) as props — those
 * refs change every time *any* card expands or follows, but we only
 * care about this card's slice. Custom equality compares the boolean
 * derived from the Set/Map plus the mutable fields on the event.
 */
const ActivityEventCard = memo(function ActivityEventCard({
  event,
  index,
  onCopy,
  onTrade,
  onVote,
  expandedEvents,
  toggleEventExpansion,
  followedTraders,
  onToggleCopy,
}: {
  event: ActivityEvent;
  index: number;
  onCopy: (event: ActivityEvent) => void;
  onTrade: (event: ActivityEvent) => void;
  onVote: (eventId: string, voteType: number, sourceTable: string) => void;
  expandedEvents: Set<string>;
  toggleEventExpansion: (eventId: string) => void;
  followedTraders: Map<string, { leaderId: string; enabled: boolean }>;
  onToggleCopy: (wallet: string) => void;
}) {
  // index + onTrade are received so the parent can pass the same prop
  // shape it used inline. They're unused inside the dispatcher but
  // *are* compared by the equality function below, so removing them
  // would defeat the memoization for those slices.
  void index;
  void onTrade;

  if (event.event_type === "user_trade") {
    return (
      <UserTradeRow
        event={event}
        onCopy={onCopy}
        onVote={onVote}
        followedTraders={followedTraders}
        onToggleCopy={onToggleCopy}
      />
    );
  }

  if (event.event_type === "copy_trade") {
    return (
      <CopyTradeRow
        event={event}
        onCopy={onCopy}
        onVote={onVote}
        followedTraders={followedTraders}
        onToggleCopy={onToggleCopy}
      />
    );
  }

  if (
    event.event_type === "user_post" ||
    event.event_type === "user_post_trade" ||
    event.event_type === "user_post_opinion" ||
    event.source_table === "post"
  ) {
    return <UserPostRow event={event} onVote={onVote} />;
  }

  if (event.event_type === "tracker_trade") {
    return (
      <TrackerTradeRow
        event={event}
        onCopy={onCopy}
        onVote={onVote}
        followedTraders={followedTraders}
        onToggleCopy={onToggleCopy}
      />
    );
  }

  if (event.event_type === "whale_trade") {
    return (
      <WhaleTradeRow
        event={event}
        onCopy={onCopy}
        onVote={onVote}
        followedTraders={followedTraders}
        onToggleCopy={onToggleCopy}
      />
    );
  }

  if (event.event_type === "new_market") {
    return <NewMarketRow event={event} />;
  }

  if (event.event_type === "new_event") {
    return (
      <NewEventRow
        event={event}
        expandedEvents={expandedEvents}
        toggleEventExpansion={toggleEventExpansion}
      />
    );
  }

  if (event.event_type === "market_move") {
    return <MarketMoveRow event={event} onVote={onVote} />;
  }

  if (event.event_type === "blog_article") {
    return <BlogArticleRow event={event} onVote={onVote} />;
  }

  // Fallback — pnl_win, pnl_loss, follow, resolution, leaderboard_move,
  // streak, and any new event type we haven't given a dedicated row.
  return <DefaultRow event={event} onVote={onVote} />;
}, (prev, next) => {
  // Skip re-render iff every slice this card depends on is unchanged.
  // The event ref is stable when handleVote returns the same object for
  // unchanged events; followedTraders ref changes only on copy-toggle
  // (rare). The Set membership check is cheap and covers expansion.
  if (prev.event !== next.event) return false;
  if (prev.index !== next.index) return false;
  if (prev.onCopy !== next.onCopy) return false;
  if (prev.onTrade !== next.onTrade) return false;
  if (prev.onVote !== next.onVote) return false;
  if (prev.toggleEventExpansion !== next.toggleEventExpansion) return false;
  if (prev.onToggleCopy !== next.onToggleCopy) return false;
  if (prev.followedTraders !== next.followedTraders) return false;
  if (
    prev.expandedEvents.has(prev.event.id) !==
    next.expandedEvents.has(next.event.id)
  ) {
    return false;
  }
  return true;
});

export default ActivityEventCard;
